<template>
  <div style="height: 320px">
    <canvas ref="canvasRef"></canvas>
  </div>
</template>

<script lang="ts" setup>
  import { ref, onMounted, PropType, computed } from 'vue';
  import { ChartData, Chart, BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend, Title } from 'chart.js';

  import { VertragCountByPartner } from '@/models/VertragModels';

  const props = defineProps({
    neuVertraege: {
      type: Array as PropType<VertragCountByPartner[]>,
      required: true,
    },
    nachtraege: {
      type: Array as PropType<VertragCountByPartner[]>,
      required: true,
    },
  });

  Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend, Title);

  const canvasRef = ref<HTMLCanvasElement | null>(null);

  const chartData = computed<ChartData<'bar'>>(() => {
    const labels = getAllUniquePartnerNames();
    const nachtraegeData = getNachtraegeData(labels);
    const neuVertraegeData = getNeuVertraegeData(labels);

    return {
      labels,
      datasets: [
        {
          label: 'Neuverträge',
          data: neuVertraegeData,
          backgroundColor: '#f1a82c',
          // barThickness: 60,
          maxBarThickness: 60,
        },
        {
          label: 'Nachträge',
          data: nachtraegeData,
          backgroundColor: '#1b3c7d',
          // barThickness: 60,
          maxBarThickness: 60,
        },
      ],
    };
  });

  function getAllUniquePartnerNames(): string[] {
    const partnerNames = new Set<string>();
    props.neuVertraege.forEach((item) => partnerNames.add(item.partnerName === '' ? '-' : item.partnerName));
    props.nachtraege.forEach((item) => partnerNames.add(item.partnerName === '' ? '-' : item.partnerName));

    return Array.from(partnerNames).sort((a, b) => a.localeCompare(b));
  }

  function getNachtraegeData(labels: string[]): number[] {
    return labels.map((label) => {
      if (label === '-') {
        const emptyNachtrag = props.nachtraege.find((item) => item.partnerName === '');
        return emptyNachtrag ? emptyNachtrag.count : 0;
      } else {
        const nachtrag = props.nachtraege.find((item) => item.partnerName === label);
        return nachtrag ? nachtrag.count : 0;
      }
    });
  }

  function getNeuVertraegeData(labels: string[]): number[] {
    return labels.map((label) => {
      if (label === '-') {
        const emptyNeuVertrag = props.neuVertraege.find((item) => item.partnerName === '');
        return emptyNeuVertrag ? emptyNeuVertrag.count : 0;
      } else {
        const neuVertrag = props.neuVertraege.find((item) => item.partnerName === label);
        return neuVertrag ? neuVertrag.count : 0;
      }
    });
  }

  onMounted(() => {
    if (canvasRef.value) {
      new Chart(canvasRef.value, {
        type: 'bar',
        data: chartData.value,
        options: {
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: 'Neuverträge und Nachträge pro Partner',
            },
            legend: {
              position: 'bottom',
            },
            tooltip: {
              enabled: true,
            },
          },
          responsive: true,
          scales: {
            x: {
              stacked: true,
            },
            y: {
              stacked: true,
            },
          },
        },
      });
    }
  });
</script>
